import { usePlateEditor } from 'platejs/react';
import {
    BoldPlugin,
    ItalicPlugin,
    UnderlinePlugin,
    StrikethroughPlugin,
    CodePlugin,
    H1Plugin,
    H2Plugin,
    H3Plugin,
    BlockquotePlugin,
    HighlightPlugin,
    KbdPlugin,
    SubscriptPlugin,
    SuperscriptPlugin,
    HorizontalRulePlugin,
} from '@platejs/basic-nodes/react';
import { CalloutPlugin } from '@platejs/callout/react';
import { ColumnPlugin, ColumnItemPlugin } from '@platejs/layout/react';
import {
    FontColorPlugin,
    FontFamilyPlugin,
    FontSizePlugin,
    FontBackgroundColorPlugin,
    TextAlignPlugin,
    LineHeightPlugin,
} from '@platejs/basic-styles/react';
import { TablePlugin, TableRowPlugin, TableCellPlugin, TableCellHeaderPlugin } from '@platejs/table/react';
import { IndentPlugin } from '@platejs/indent/react';
import { ListPlugin } from '@platejs/list/react';
import { BulletedListRules, OrderedListRules } from '@platejs/list';
import { LinkPlugin } from '@platejs/link/react';
import { MentionPlugin, MentionInputPlugin } from '@platejs/mention/react';
import { TrailingBlockPlugin, ExitBreakPlugin, KEYS } from 'platejs';
import type { Value } from 'platejs';
import { EMPTY_EDITOR_VALUE } from '../types/types';
import { TableElement, TableRowElement, TableCellElement, TableCellHeaderElement } from '../components/TableElements/tableElements';
import { findInterVoidInsertIndex, emptyParagraph } from './interVoidParagraph';
import { BlockList } from '../components/BlockList/BlockList';
import { LinkElement } from '../components/LinkElement/LinkElement';
import { ImagePlugin } from '../components/ImagePlugin/ImagePlugin';
import { HrElement } from '../components/HrElement/HrElement';
import { ColumnGroupElement } from '../components/ColumnGroupElement/ColumnGroupElement';
import { ColumnElement } from '../components/ColumnElement/ColumnElement';
// import { MentionElement } from '../components/MentionElement/MentionElement';
// import { MentionInputElement } from '../components/MentionElement/MentionInputElement';

interface UsePlateEditorLogicArgs {
    initialValue?: Value;
    /**
     * When non-empty, registers the @platejs/mention plugin. The actual
     * list is consumed by MentionInputElement via PlateEditorConfigContext —
     * passing only the toggle here so the editor's plugin set is stable
     * across re-renders (changing plugins recreates the editor).
     */
    enableMentions?: boolean;
}

const INLINE_STYLE_TARGETS: string[] = [
    ...((KEYS as any).heading ?? []),
    (KEYS as any).p,
    (KEYS as any).blockquote,
    (KEYS as any).td,
    (KEYS as any).th,
    (KEYS as any).callout,
].filter(Boolean);

// Excludes td/th — putting them here causes double-bullet rendering inside cells.
const LIST_INDENT_TARGETS: string[] = [
    ...((KEYS as any).heading ?? []),
    (KEYS as any).p,
    (KEYS as any).blockquote,
    (KEYS as any).callout,
].filter(Boolean);

export function usePlateEditorLogic({ initialValue, enableMentions = false }: UsePlateEditorLogicArgs = {}) {
    const editor = usePlateEditor({
        plugins: [
            ...(enableMentions
                ? [
                      // @platejs/mention configured with `insertSpaceAfterMention` to
                      // match the existing comments UX (mention + trailing
                      // space). The combobox UI lives in MentionInputElement, which
                      // reads the user list from PlateEditorConfigContext.
                      //   (MentionPlugin as any).configure({ options: { insertSpaceAfterMention: true } }).withComponent(MentionElement),
                      //   (MentionInputPlugin as any).withComponent(MentionInputElement),
                      // UNCOMMENT THIS LINE TO ENABLE MENTION PLUGIN
                  ]
                : []),
            BoldPlugin,
            ItalicPlugin,
            UnderlinePlugin,
            StrikethroughPlugin,
            CodePlugin,
            HighlightPlugin,
            KbdPlugin,
            SubscriptPlugin,
            SuperscriptPlugin,
            H1Plugin,
            H2Plugin,
            H3Plugin,
            BlockquotePlugin,
            // HrElement wraps <hr/> to avoid React's "void element with children" error.
            (HorizontalRulePlugin as any).withComponent(HrElement),
            CalloutPlugin,
            (FontColorPlugin as any).configure({ inject: { targetPlugins: INLINE_STYLE_TARGETS } }),
            (FontBackgroundColorPlugin as any).configure({ inject: { targetPlugins: INLINE_STYLE_TARGETS } }),
            (FontSizePlugin as any).configure({ inject: { targetPlugins: INLINE_STYLE_TARGETS } }),
            (FontFamilyPlugin as any).configure({ inject: { targetPlugins: INLINE_STYLE_TARGETS } }),
            (TextAlignPlugin as any).configure({ inject: { targetPlugins: [...INLINE_STYLE_TARGETS, 'image'] } }),
            // Block-level line-height — drives the LineHeightMenu toolbar.
            // Defaults to 1.2 to match the tight list rhythm in .content; user
            // can override per-block via the dropdown.
            (LineHeightPlugin as any).configure({
                inject: { targetPlugins: INLINE_STYLE_TARGETS, nodeProps: { defaultNodeValue: 1.2 } },
            }),
            (LinkPlugin as any).withComponent(LinkElement),
            (IndentPlugin as any).configure({ inject: { targetPlugins: LIST_INDENT_TARGETS }, options: { offset: 24 } }),
            // Two list normalizers, both required to kill the double-bullet bug
            // inside table cells:
            //
            //   1. Strip listStyleType/listStart from non-target nodes (td/th/
            //      table/tr/column_group/column/image). Guards against persisted
            //      bad data or library edge cases that land list props on a
            //      container — BlockList would otherwise double-wrap.
            //
            //   2. Lift child <p> blocks that carry listStyleType OUT of a
            //      parent <p> that also carries listStyleType. The slate→plate
            //      converter used to emit nested Slate lists as CHILDREN of the
            //      outer list-item's <p> instead of as SIBLINGS. Persisted docs
            //      written under that bug still have the bad shape, so BlockList
            //      wraps the outer <p> in <ul><li> AND every inner <p> in its
            //      own <ul><li> — two visible bullets per nested-bullet line.
            //      The fix walks the children of any list-style <p> and pulls
            //      every child block out to be a SIBLING, dropping inline cruft
            //      back into the parent.
            (ListPlugin as any)
                .configure({
                    inject: { targetPlugins: LIST_INDENT_TARGETS },
                    render: { belowNodes: BlockList },
                    // Markdown-style input rules: `1. ` / `1) ` → numbered
                    // list, `- ` / `* ` → bullet list, fired on a trailing
                    // space at the start of a block.
                    inputRules: [
                        OrderedListRules.markdown({ variant: '.' }),
                        OrderedListRules.markdown({ variant: ')' }),
                        BulletedListRules.markdown({ variant: '-' }),
                        BulletedListRules.markdown({ variant: '*' }),
                    ],
                })
                .overrideEditor(({ editor: ed, tf: { normalizeNode } }: any) => ({
                    transforms: {
                        normalizeNode([n, path]: any) {
                            // (1) Strip stray list props from non-target node types.
                            if (n?.type && !LIST_INDENT_TARGETS.includes(n.type)) {
                                if (n.listStyleType != null || n.listStart != null) {
                                    ed.tf.unsetNodes(['listStyleType', 'listStart'], { at: path });
                                    return;
                                }
                            }
                            // (1b) toggleList from @platejs/list flags every block in
                            // the selection — including empty separator paragraphs
                            // legacy table cells use for visual spacing. Those become
                            // empty bulleted rows interleaved with real bullets.
                            // Delete an empty list-style <p> ONLY when it's
                            // sandwiched between two real list items of the same
                            // style (a true "selector-artifact separator"). Earlier
                            // OR-logic also matched the fresh empty <p> Plate creates
                            // on Enter (prev = "hello" real item, next = trailing
                            // <p> from TrailingBlockPlugin), so the bullet vanished
                            // the instant the user hit Enter. The AND form preserves
                            // continuation while still scrubbing the
                            // selected-across-gap case (both neighbors are real).
                            if (
                                n?.type &&
                                LIST_INDENT_TARGETS.includes(n.type) &&
                                n.listStyleType != null &&
                                Array.isArray(n.children) &&
                                n.children.every(
                                    (c: any) => c && typeof c === 'object' && typeof c.text === 'string' && c.text.trim() === '',
                                )
                            ) {
                                // Skip when the empty block IS the current
                                // selection target — pressing Enter in the
                                // middle of a list creates exactly this shape
                                // (empty new item flanked by two real items),
                                // and deleting it makes Enter feel like it
                                // jumped into the next item instead of creating
                                // a new one.
                                const selAnchor = ed.selection?.anchor?.path;
                                const isCaretInside =
                                    Array.isArray(selAnchor) &&
                                    selAnchor.length >= path.length &&
                                    path.every((seg: number, i: number) => seg === selAnchor[i]);
                                if (isCaretInside) {
                                    return normalizeNode([n, path]);
                                }
                                const parentEntry = ed.api?.parent?.(path);
                                const parent = parentEntry?.[0];
                                if (parent && Array.isArray(parent.children)) {
                                    const idx = path[path.length - 1];
                                    const hasNext = idx + 1 < parent.children.length;
                                    if (hasNext) {
                                        const isRealListItem = (sib: any) =>
                                            sib &&
                                            sib.listStyleType === n.listStyleType &&
                                            Array.isArray(sib.children) &&
                                            sib.children.some(
                                                (c: any) =>
                                                    c && typeof c === 'object' && typeof c.text === 'string' && c.text.trim() !== '',
                                            );
                                        const prev = idx > 0 ? parent.children[idx - 1] : null;
                                        const next = parent.children[idx + 1];
                                        if (isRealListItem(prev) && isRealListItem(next)) {
                                            ed.tf.removeNodes({ at: path });
                                            return;
                                        }
                                    }
                                }
                            }
                            // (2) Lift nested <p> (or any block child) out of a
                            // list-style <p>. <p> shouldn't contain block children
                            // in Plate's data model anyway; doing so here also
                            // produces the bullet shape the user expects.
                            //
                            // Legacy Slate docs sometimes interleave empty <p>'s as
                            // visual separators between real bullets. Lifting those
                            // as siblings produces empty bullet lines (bullet + no
                            // text). We DROP them: remove from parent without
                            // re-inserting. Real (non-empty) blocks lift as siblings.
                            if (n?.type === 'p' && n.listStyleType && Array.isArray(n.children)) {
                                // Identify block-child indices. Skip inline link
                                // nodes ('a') — those belong inside the paragraph.
                                const blockIdx: number[] = [];
                                for (let i = 0; i < n.children.length; i++) {
                                    const c = n.children[i];
                                    if (c && typeof c === 'object' && c.type && c.type !== 'a') blockIdx.push(i);
                                }
                                if (blockIdx.length > 0) {
                                    const parentEntry = ed.api?.parent?.(path);
                                    if (parentEntry) {
                                        const parentPath = parentEntry[1];
                                        const indexInParent = path[path.length - 1];
                                        const isBlockEmpty = (b: any): boolean => {
                                            if (!b || !Array.isArray(b.children) || b.children.length === 0) return true;
                                            return b.children.every(
                                                (c: any) =>
                                                    c && typeof c === 'object' && typeof c.text === 'string' && c.text.trim() === '',
                                            );
                                        };
                                        // Snapshot block children (source order) and partition
                                        // into "real" (lift to siblings) and "empty" (drop).
                                        const realBlocks: any[] = [];
                                        for (const i of blockIdx) {
                                            const child = n.children[i];
                                            if (!isBlockEmpty(child)) realBlocks.push({ ...child });
                                        }
                                        const parentIndent = (n.indent ?? 1) as number;
                                        const parentListStyleType = n.listStyleType;
                                        ed.tf.withoutNormalizing(() => {
                                            // Remove ALL block children from the parent (both
                                            // empty and real) in reverse order so earlier indices
                                            // stay valid as we delete.
                                            for (let k = blockIdx.length - 1; k >= 0; k--) {
                                                ed.tf.removeNodes({ at: [...path, blockIdx[k]] });
                                            }
                                            // Insert only the REAL (non-empty) lifted blocks as
                                            // siblings after the parent. Each inherits
                                            // listStyleType (if missing) and bumps indent so
                                            // visual nesting is preserved.
                                            realBlocks.forEach((b, i) => {
                                                const sibling: any = { ...b };
                                                if (sibling.listStyleType == null) sibling.listStyleType = parentListStyleType;
                                                if (sibling.indent == null) sibling.indent = parentIndent + 1;
                                                ed.tf.insertNodes(sibling, { at: [...parentPath, indexInParent + 1 + i] });
                                            });
                                        });
                                        return;
                                    }
                                }
                            }
                            return normalizeNode([n, path]);
                        },
                    },
                })),
            ImagePlugin,
            // Flatten nested column_groups to prevent broken layouts.
            (ColumnPlugin as any)
                .overrideEditor(({ editor, tf: { normalizeNode }, type }: any) => ({
                    transforms: {
                        normalizeNode([n, path]: any) {
                            if (n?.type === type && path.length > 1) {
                                const parent = (editor as any).api?.parent?.(path);
                                const parentType = parent?.[0]?.type;
                                if (parentType === 'column' || parentType === type) {
                                    (editor as any).tf.unwrapNodes({ at: path });
                                    return;
                                }
                            }
                            return normalizeNode([n, path]);
                        },
                    },
                }))
                .withComponent(ColumnGroupElement),
            // Auto-assign column IDs so drag-resize survives reloads.
            (ColumnItemPlugin as any)
                .overrideEditor(({ editor, tf: { normalizeNode }, type }: any) => ({
                    transforms: {
                        normalizeNode([n, path]: any) {
                            if (n?.type === type && !n.id) {
                                const id = `col-${Math.random().toString(36).slice(2, 10)}`;
                                (editor as any).tf.setNodes({ id }, { at: path });
                                return;
                            }
                            return normalizeNode([n, path]);
                        },
                    },
                }))
                .withComponent(ColumnElement),
            // Guarantee an editable caret target between/around top-level block
            // voids. Tables and <hr> are void elements (no caret inside), and
            // TrailingBlockPlugin only fixes the document TAIL — so two stacked
            // tables (or an interior divider) have no caret target between them.
            // This root-level normalizer wedges ONE empty paragraph per pass at
            // the first offending interior adjacency and returns, letting Slate
            // re-normalize for any remaining gaps. Scope is strictly interior
            // (we never touch the last node) so it can't fight TrailingBlock,
            // and it only inserts between two voids — the inserted paragraph is
            // editable, so the same gap can't re-fire.
            (TablePlugin as any)
                .overrideEditor(({ editor, tf: { normalizeNode } }: any) => ({
                    transforms: {
                        normalizeNode([n, path]: any) {
                            if (Array.isArray(path) && path.length === 0 && Array.isArray(n?.children)) {
                                const insertAt = findInterVoidInsertIndex(n.children);
                                if (insertAt != null) {
                                    (editor as any).tf.insertNodes(emptyParagraph(), { at: [insertAt] });
                                    return;
                                }
                            }
                            return normalizeNode([n, path]);
                        },
                    },
                }))
                .withComponent(TableElement),
            (TableRowPlugin as any).withComponent(TableRowElement),
            (TableCellPlugin as any).withComponent(TableCellElement),
            (TableCellHeaderPlugin as any).withComponent(TableCellHeaderElement),
            TrailingBlockPlugin,
            ExitBreakPlugin,
        ],
        value: initialValue ?? EMPTY_EDITOR_VALUE,
    });
    return { editor };
}
